/**
 * Head Office API → widget-prop mappers.
 *
 * Each head-office endpoint returns its own shape; the presentational widgets
 * under `headOffice/` expect the shapes declared in `data.ts`. These pure
 * functions bridge the two so the widgets stay unchanged. Keep every mapper
 * defensive — the API envelope may omit fields when there is no data.
 */

import {
  CampusDatum,
  GrowthMetric,
  KpiDatum,
  MixDimension,
  MixSlice,
  RangeDatum,
  StudentDatum,
  TeacherDatum,
  fmt,
  fmtMins,
  pct,
} from "./data";
import { ModalData } from "./modals/modalBuilders";

/* ------------------------------------------------------------------ *
 * Shared helpers
 * ------------------------------------------------------------------ */

/** Palette reused for donut slices and growth lines. */
const PALETTE = [
  "#9A469B",
  "#EF437B",
  "#1897A6",
  "#F9A11B",
  "#C2D52E",
  "#58C0BF",
  "#4F6C8F",
  "#8CA00E",
];

/** Two-letter avatar initials from a campus / person name. */
export const initialsFrom = (name = ""): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const round = (n: any): number => Math.round(Number(n) || 0);

/**
 * adoption_rate (and similar) already come on a 0–100 percent scale — e.g.
 * 0.09 means 0.09% (4 of 4505 readers), 76.4 means 76.4%. Keep up to 2 decimals
 * so genuinely tiny values don't collapse to a misleading "0%".
 */
const pctVal = (n: any): number => Math.round((Number(n) || 0) * 100) / 100;

/** Compact big numbers (e.g. 1_600_000 → "1.60M", 45_210 → "45,210"). */
const big = (n: any): string => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(2) + "M";
  return v.toLocaleString("en-US");
};

interface ApiTrend {
  direction?: "up" | "down" | "neutral";
  percentage?: number;
  current?: number;
  previous?: number;
}

/** Signed percentage from an API trend object (down → negative). */
const trendPct = (t?: ApiTrend): number => {
  if (!t) return 0;
  const p = Math.abs(round(t.percentage));
  return t.direction === "down" ? -p : p;
};

const trendUp = (t?: ApiTrend): boolean => !t || t.direction !== "down";

const signed = (n: number, suffix = ""): string =>
  (n >= 0 ? "+" : "") + n + suffix;

/* ------------------------------------------------------------------ *
 * Campus factory — widgets expect the rich CampusDatum, but each
 * endpoint only supplies a subset. Fill the rest with safe defaults.
 * ------------------------------------------------------------------ */

const CAMPUS_DEFAULTS: CampusDatum = {
  name: "",
  initials: "",
  students: 0,
  books: 0,
  pages: 0,
  classes: 0,
  quiz: 0,
  adoption: 0,
  trend: 0,
  ageApprop: 0,
  completion: 0,
  active: 0,
  assigned: 0,
  inProgress: 0,
  overdue: 0,
  weekly: [],
  tLogins: 0,
  tAssignments: 0,
  tActive: 0,
  tTotal: 0,
  classesTop: [],
  studentsTop: [],
  teachersTop: [],
};

const makeCampus = (p: Partial<CampusDatum>): CampusDatum => ({
  ...CAMPUS_DEFAULTS,
  ...p,
});

const maxBooksOf = (campuses: CampusDatum[]): number =>
  Math.max(1, ...campuses.map((c) => c.books));

/* ------------------------------------------------------------------ *
 * 1. Dashboard → KPI strip
 * ------------------------------------------------------------------ */

export const mapKpis = (data: any): KpiDatum[] => {
  const s = data?.stats ?? {};
  const absDelta = (t?: ApiTrend) => {
    const d = round(t?.current) - round(t?.previous);
    return signed(d);
  };
  const kpi = (
    stat: any,
    label: string,
    icon: KpiDatum["icon"],
    windowed: boolean,
    valueFmt: (n: any) => string = fmt,
  ): KpiDatum => ({
    label,
    value: valueFmt(stat?.value),
    icon,
    delta: windowed
      ? signed(trendPct(stat?.trend), "%")
      : absDelta(stat?.trend),
    up: trendUp(stat?.trend),
  });

  return [
    kpi(s.active_campuses, "Total Campuses", "campus", false),
    kpi(s.enrolled_teachers, "Total Teachers", "teacher", false),
    kpi(s.enrolled_students, "Total Students", "students", false),
    kpi(s.books_read, "Total Books Read", "book", true),
    kpi(s.pages_read, "Total Pages Read", "pages", true, big),
    // screen time closes the strip, same order as the campus dashboards
    kpi(s.screen_time_minutes, "Total Screen Time", "time", true, fmtMins),
  ];
};

/* ------------------------------------------------------------------ *
 * 2. Reader Campus (list)
 * ------------------------------------------------------------------ */

export const mapReaderCampuses = (
  data: any,
): { campuses: CampusDatum[]; maxBooks: number } => {
  const campuses = (data?.campuses ?? []).map((c: any) =>
    makeCampus({
      campus_id: c.campus_id,
      name: c.campus_name,
      initials: initialsFrom(c.campus_name),
      students: Number(c.active_enrolled_students) || 0,
      books: Number(c.total_books_read) || 0,
      classes: Number(c.total_classes) || 0,
      adoption: pctVal(c.adoption_rate),
      trend: round(trendPct(c.trend)),
    }),
  );
  return { campuses, maxBooks: maxBooksOf(campuses) };
};

/* ------------------------------------------------------------------ *
 * 3. Reader Campus detail → drill-down modal
 * ------------------------------------------------------------------ */

export const buildCampusModalFromDetail = (d: any): ModalData => {
  const quiz = d?.quiz ?? {};
  const assigned = Number(d?.assigned_books) || 0;
  const denom = assigned || 1;
  return {
    kind: "campus",
    cert: true,
    title: d?.campus_name ?? "Campus",
    subtitle: `${d?.total_classes ?? 0} classrooms · ${fmt(
      d?.enrolled_students ?? 0,
    )} students · ${pctVal(d?.adoption_rate)}% adoption`,
    stats: [
      { value: fmt(d?.total_books_completed ?? 0), label: "Books completed" },
      { value: pctVal(d?.adoption_rate) + "%", label: "Adoption" },
      { value: round(quiz.passed_percentage) + "%", label: "Quiz pass" },
    ],
    stats2: [
      {
        value: fmt(assigned),
        label: "Assigned books",
        color: "var(--ho-ink)",
      },
      {
        value: fmt(d?.in_progress_books ?? 0),
        label: "In progress",
        color: "#1897A6",
      },
      { value: d?.overdue_books ?? 0, label: "Overdue", color: "#B42318" },
    ],
    tabs: [
      {
        label: "Assignments",
        listTitle: "Assignment breakdown",
        rows: [
          {
            rank: 1,
            name: "Completed",
            hasSub: false,
            value: fmt(d?.total_books_completed ?? 0),
            bar: pct(Number(d?.total_books_completed) || 0, denom),
          },
          {
            rank: 2,
            name: "In progress",
            hasSub: false,
            value: fmt(d?.in_progress_books ?? 0),
            bar: pct(Number(d?.in_progress_books) || 0, denom),
          },
          {
            rank: 3,
            name: "Overdue",
            hasSub: false,
            value: fmt(d?.overdue_books ?? 0),
            bar: pct(Number(d?.overdue_books) || 0, denom),
          },
          {
            rank: 4,
            name: "Quiz attempted",
            hasSub: false,
            value: fmt(quiz.attempted ?? 0),
            bar: pct(Number(quiz.passed) || 0, Number(quiz.attempted) || 1),
          },
          {
            rank: 5,
            name: "Quiz passed",
            hasSub: false,
            value: fmt(quiz.passed ?? 0),
            bar: pct(Number(quiz.passed) || 0, Number(quiz.attempted) || 1),
          },
        ],
      },
    ],
  };
};

/** Build the drill-down modal from the /head-office/student/{id} detail. */
/**
 * Campus drill-down with the three lenses of
 * `head-office/campus/{id}?type=class|student|teacher`.
 * Header/stats come from the campus detail; each lens fills one tab.
 * Field names are read defensively (the lenses mirror the other endpoints).
 */
export const buildCampusModalFromParts = (
  d: any,
  classesData: any,
  studentsData: any,
  teachersData: any,
): ModalData => {
  const base = buildCampusModalFromDetail(d);

  const classRows = (classesData?.classes ?? []).map((c: any, i: number) => ({
    books: Number(c.completed_books ?? c.total_books_read) || 0,
    name: c.class_name ?? c.name ?? `Class ${i + 1}`,
  }));
  const maxClass = Math.max(1, ...classRows.map((r: any) => r.books));

  const studentRows = (studentsData?.students ?? []).map((s: any) => ({
    books: Number(s.completed_books ?? s.total_books_read) || 0,
    name: s.name,
    sub: s.class_name ?? "",
  }));
  const maxStudent = Math.max(1, ...studentRows.map((r: any) => r.books));

  const teacherRows = (teachersData?.teachers ?? []).map((t: any) => {
    const assigned = Number(t.assigned_books) || 0;
    const completed = Number(t.completed_books) || 0;
    return {
      name: t.name,
      assigned,
      completed,
      quiz: round(t.quiz?.passed_percentage),
      rate: assigned ? Math.round((completed / assigned) * 100) : 0,
    };
  });

  const tabs = [];
  if (classRows.length) {
    tabs.push({
      label: "Classrooms",
      listTitle: "The Reader Classrooms",
      rows: classRows.map((r: any, i: number) => ({
        rank: i + 1,
        name: r.name,
        hasSub: false,
        value: fmt(r.books),
        bar: pct(r.books, maxClass),
      })),
    });
  }
  if (studentRows.length) {
    tabs.push({
      label: "Students",
      listTitle: "The Reader Students",
      rows: studentRows.map((r: any, i: number) => ({
        rank: i + 1,
        name: r.name,
        hasSub: !!r.sub,
        sub: r.sub,
        value: r.books + " books",
        bar: pct(r.books, maxStudent),
      })),
    });
  }
  if (teacherRows.length) {
    tabs.push({
      label: "Teachers",
      listTitle: "Teacher Performance",
      rows: teacherRows.map((r: any, i: number) => ({
        rank: i + 1,
        name: r.name,
        hasSub: true,
        sub: `${fmt(r.completed)}/${fmt(r.assigned)} completed · ${r.quiz}% quiz`,
        value: r.rate + "%",
        bar: r.rate + "%",
      })),
    });
  }

  return { ...base, tabs: tabs.length ? tabs : base.tabs };
};

export const buildStudentModalFromDetail = (d: any): ModalData => {
  const quiz = d?.quiz ?? {};
  const age = d?.age_appropriate ?? {};
  const assigned = Number(d?.assigned_books) || 0;
  const completed = Number(d?.completed_books) || 0;
  const denom = assigned || 1;
  const completionRate = round(d?.book_completion_rate);
  const agePct = round(age.percentage);
  const quizPct = round(quiz.passed_percentage);
  // `badges_count` is authoritative; fall back to the badges array length
  const badges =
    Number(d?.badges_count ?? (Array.isArray(d?.badges) ? d.badges.length : 0)) ||
    0;
  return {
    kind: "student",
    cert: false,
    title: d?.name ?? "Student",
    subtitle: [d?.campus_name, d?.class_name].filter(Boolean).join(" · "),
    // uniform cards; rates (completion / age / quiz) live in the Progress list
    stats: [
      { value: fmt(assigned), label: "Assigned books" },
      { value: fmt(completed), label: "Books read" },
      { value: fmt(Number(d?.pages_read) || 0), label: "Pages read" },
      { value: fmtMins(d?.screen_time_minutes), label: "Screen time" },
      { value: fmt(badges), label: "Badges earned" },
    ],
    stats2: [],
    tabs: [
      {
        label: "Progress",
        listTitle: "Progress highlights",
        rows: [
          {
            rank: 1,
            name: "Books completed",
            hasSub: false,
            value: fmt(completed),
            bar: pct(completed, denom),
          },
          {
            rank: 2,
            name: "Assignments completed",
            hasSub: false,
            value: completionRate + "%",
            bar: completionRate + "%",
          },
          {
            rank: 3,
            name: "Age-appropriate reading",
            hasSub: false,
            value: agePct + "%",
            bar: agePct + "%",
          },
          {
            rank: 4,
            name: "Quiz pass rate",
            hasSub: false,
            value: quizPct + "%",
            bar: quizPct + "%",
          },
        ],
      },
    ],
  };
};

/* ------------------------------------------------------------------ *
 * 4. Top Teachers
 * ------------------------------------------------------------------ */

export const mapTopTeachers = (data: any): TeacherDatum[] =>
  (data?.teachers ?? []).map((t: any) => ({
    teacher_id: Number(t.teacher_id ?? t.id ?? t.user_id) || undefined,
    name: t.name,
    campus: t.campus_name,
    assigned: Number(t.assigned_books) || 0,
    completed: Number(t.completed_books) || 0,
    quiz: round(t.quiz?.passed_percentage),
  }));

/** bar widths must never exceed the track (books read can outrun assigned) */
const barPct = (v: number, max: number): string =>
  Math.max(0, Math.min(100, max > 0 ? Math.round((v / max) * 100) : 0)) + "%";

/**
 * `GET school/teacher/{teacherId}` → drill-down modal.
 * The payload nests its numbers under `stats`; top-level aliases are kept as a
 * fallback in case another role returns the flatter student-detail shape.
 * Note the response carries no teacher name — hence `fallbackName` from the row.
 */
export const buildTeacherModalFromDetail = (
  d: any,
  fallbackName?: string,
): ModalData => {
  const s = d?.stats ?? {};
  const assigned = Number(s.total_assigned_books ?? d?.assigned_books) || 0;
  const read = Number(s.total_books_read ?? d?.completed_books) || 0;
  const pages = Number(s.total_pages_read ?? d?.pages_read) || 0;
  const students = Number(s.total_students ?? d?.students_count) || 0;
  const classList: any[] = Array.isArray(d?.classes) ? d.classes : [];
  const overdue = Number(d?.overdue_books ?? s.overdue_books) || 0;
  const completionRate = round(
    s.book_completion_rate ?? d?.book_completion_rate,
  );
  const quizPct = round(
    s.quiz_passing_percentage ?? d?.quiz?.passed_percentage,
  );

  return {
    kind: "campus",
    cert: false,
    title: d?.name ?? fallbackName ?? "Teacher",
    // class names read better than a bare count, and match the Top Teachers row
    subtitle:
      classList
        .map((c: any) => c.class_name ?? c.name)
        .filter(Boolean)
        .join(" · ") ||
      [d?.campus_name, d?.class_name].filter(Boolean).join(" · "),
    stats: [
      { value: fmt(read), label: "Books read" },
      { value: fmt(assigned), label: "Books assigned" },
      { value: fmt(pages), label: "Pages read" },
      { value: fmt(students), label: "Students" },
    ],
    stats2: [],
    tabs: [
      {
        label: "Performance",
        listTitle: "Teaching performance",
        rows: [
          {
            rank: 1,
            name: "Books read",
            hasSub: true,
            sub: `across ${fmt(classList.length || Number(s.total_classes) || 0)} classroom(s)`,
            value: fmt(read),
            bar: barPct(read, Math.max(read, assigned)),
          },
          {
            rank: 2,
            name: "Completion rate",
            hasSub: true,
            sub: `of ${fmt(assigned)} assigned books`,
            value: completionRate + "%",
            bar: barPct(completionRate, 100),
          },
          {
            rank: 3,
            name: "Quiz pass rate",
            hasSub: false,
            value: quizPct + "%",
            bar: barPct(quizPct, 100),
          },
          {
            rank: 4,
            name: "Overdue books",
            hasSub: false,
            value: fmt(overdue),
            bar: barPct(overdue, assigned || 1),
          },
        ],
      },
    ],
  };
};

/* ------------------------------------------------------------------ *
 * 5. Top Students
 * ------------------------------------------------------------------ */

export const mapTopStudents = (data: any): StudentDatum[] =>
  (data?.students ?? []).map((s: any) => ({
    student_id: Number(s.student_id ?? s.id ?? s.user_id) || undefined,
    name: s.name,
    meta: [s.campus_name, s.class_name].filter(Boolean).join(" · "),
    initials: initialsFrom(s.name),
    books: Number(s.completed_books) || 0,
    badges: Number(s.badges_count) || 0,
    // pages / time / quiz are not returned by the top-students endpoint
    pages: 0,
    time: "—",
    quiz: 0,
  }));

/* ------------------------------------------------------------------ *
 * 6. Adoption Rate
 * ------------------------------------------------------------------ */

export interface AdoptionView {
  percent: number;
  caption: string;
  deltaPct: string;
  ageAppropriate: string;
  /** enrolled students — 0 means there's no adoption data to show */
  enrolled: number;
}

export const mapAdoption = (data: any): AdoptionView => ({
  percent: pctVal(data?.adoption_rate),
  caption: `${fmt(data?.active_readers ?? 0)} active readers of ${fmt(
    data?.enrolled_students ?? 0,
  )} enrolled`,
  deltaPct: signed(round(trendPct(data?.trend)), "%"),
  ageAppropriate: round(data?.age_appropriate?.percentage) + "%",
  enrolled: Number(data?.enrolled_students) || 0,
});

/* ------------------------------------------------------------------ *
 * 7. Reading Mix
 * ------------------------------------------------------------------ */

const itemsToSlices = (items: any[] = []): MixSlice[] =>
  items.map((it, i) => ({
    name: it.name,
    pct: round(it.percentage),
    color: PALETTE[i % PALETTE.length],
  }));

export const mapReadingMix = (data: any): MixDimension[] => {
  const dims: MixDimension[] = [];
  if (data?.genres) {
    dims.push({
      key: "genre",
      label: "By Genre",
      data: itemsToSlices(data.genres.items),
    });
  }
  if (data?.languages) {
    dims.push({
      key: "language",
      label: "By Language",
      data: (data.languages.breakdown ?? []).map((b: any, i: number) => ({
        name: b.language,
        pct: round(b.percentage),
        color: PALETTE[i % PALETTE.length],
      })),
    });
  }
  if (data?.age_ranges) {
    dims.push({
      key: "age",
      label: "By Age Range",
      data: itemsToSlices(data.age_ranges.items),
    });
  }
  if (data?.keywords) {
    dims.push({
      key: "keyword",
      label: "By Keyword",
      data: itemsToSlices(data.keywords.items),
    });
  }
  return dims;
};

/* ------------------------------------------------------------------ *
 * 8. Book Content Analysis
 * ------------------------------------------------------------------ */

export interface ContentView {
  summary: {
    overallBooks: string;
    mostReadGenre: { name: string; pct: string };
    mostReadLanguage: { name: string; pct: string };
  };
  campuses: CampusDatum[];
  maxBooks: number;
}

export const mapContent = (data: any): ContentView => {
  const campuses = (data?.campuses ?? []).map((c: any) =>
    makeCampus({
      campus_id: c.campus_id,
      name: c.campus_name,
      books: Number(c.completed_books) || 0,
    }),
  );
  return {
    summary: {
      overallBooks: fmt(data?.total_completed_books ?? 0),
      mostReadGenre: {
        name: data?.top_genre?.name ?? "—",
        pct: data?.top_genre ? round(data.top_genre.percentage) + "%" : "—",
      },
      mostReadLanguage: {
        name: data?.most_read_language?.name ?? "—",
        pct: data?.most_read_language
          ? round(data.most_read_language.percentage) + "%"
          : "—",
      },
    },
    campuses,
    maxBooks: maxBooksOf(campuses),
  };
};

/* ------------------------------------------------------------------ *
 * 8b. Assignment Pipeline — rides along on the book-content-analysis payload
 * ------------------------------------------------------------------ */

export interface AssignmentView {
  statuses: { key: string; label: string; value: number; color: string }[];
  total: number;
  donePct: number;
}

const ASSIGN_COLORS = {
  not_started: "#ADB5BD",
  in_progress: "#F9A11B",
  completed: "#1897A6",
  overdue: "#EF437B",
};

/** book-content-analysis carries the assignment status counts. */
export const mapAssignmentPipeline = (data: any): AssignmentView | null => {
  if (!data) return null;
  const notStarted = Number(data.not_started_yet) || 0;
  const inProgress = Number(data.in_progress_books) || 0;
  // completed *assigned* books — `assingend_completed_books` is the API's
  // (misspelled) field for that. `total_completed_books` counts every book read,
  // assigned or not, so it doesn't belong in this donut.
  const completed = Number(data.assingend_completed_books) || 0;
  const overdue = Number(data.overdue_books) || 0;
  // centre total = assigned books; fall back to the sum of the buckets.
  const total =
    Number(data.assigned_books) ||
    notStarted + inProgress + completed + overdue;
  return {
    statuses: [
      { key: "not_started", label: "Not Started", value: notStarted, color: ASSIGN_COLORS.not_started },
      { key: "in_progress", label: "In Progress", value: inProgress, color: ASSIGN_COLORS.in_progress },
      { key: "completed", label: "Completed", value: completed, color: ASSIGN_COLORS.completed },
      { key: "overdue", label: "Overdue", value: overdue, color: ASSIGN_COLORS.overdue },
    ],
    total,
    donePct: total ? Math.round((completed / total) * 100) : 0,
  };
};

/* ------------------------------------------------------------------ *
 * 9. Campus Comparison
 * ------------------------------------------------------------------ */

export const mapComparison = (data: any): CampusDatum[] =>
  (data?.campuses ?? []).map((c: any) =>
    makeCampus({
      campus_id: c.campus_id,
      name: c.campus_name,
      adoption: pctVal(c.adoption_rate),
      quiz: round(c.quiz?.passed_percentage),
      ageApprop: round(c.age_appropriate?.percentage),
      completion: round(c.book_completion_rate),
      // "Engagement" column — the API's own engagement_score (kept to 2dp so
      // small scores like 0.5 don't collapse to 0)
      active: pctVal(c.engagement_score),
    }),
  );

/* ------------------------------------------------------------------ *
 * 10. Teacher Adoption
 * ------------------------------------------------------------------ */

export const mapTeacherAdoption = (data: any): CampusDatum[] =>
  (data?.campuses ?? []).map((c: any) =>
    makeCampus({
      campus_id: c.campus_id,
      name: c.campus_name,
      assigned: Number(c.assigned_books) || 0,
      tActive: Number(c.active_teachers) || 0,
      tTotal: Number(c.enrolled_teachers) || 0,
    }),
  );

/* ------------------------------------------------------------------ *
 * 12. Reading Growth Trend
 * ------------------------------------------------------------------ */

export interface GrowthView {
  range: RangeDatum;
  metrics: GrowthMetric[];
}

export const mapGrowth = (data: any): GrowthView => {
  const series: any[] = data?.series ?? [];
  const labels: string[] = series[0]?.points?.map((p: any) => p.label) ?? [];

  const metrics: GrowthMetric[] = series.map((s, i) => {
    const values: number[] = (s.points ?? []).map((p: any) => Number(p.value) || 0);
    return {
      key: s.key,
      label: s.label,
      color: PALETTE[i % PALETTE.length],
      unit: s.unit ?? "",
      sub: s.label,
      // the widget calls derive(range); our series is already resolved.
      derive: () => values,
    };
  });

  const range: RangeDatum = {
    label: "",
    months: labels,
    books: [],
    pages: [],
    delta: "vs previous period",
  };

  return { range, metrics };
};
